import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Warning } from 'shared/icons';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../hooks/useAuth';
import { useDelayedLoading } from '../../hooks/useDelayedLoading';
import { usePurchaseCart } from '../../hooks/usePurchaseCart';
import { api } from '../../services/api';
import { CartModal } from './components/Cart';
import { FinderOverlay } from './components/FinderOverlay';
import { PresetBuilder } from './components/PresetBuilder';
import { Toolbar } from './components/Toolbar';
import { useTokens } from './components/tokens';
import { WarehouseConfigModal } from './components/WarehouseConfigModal';
import { Workspace } from './components/Workspace';
import { IsColumnMasked as isColumnMasked } from './constants/columns';
import { DaysBetween, ResolvePeriodRange } from './periodHelpers';
import { GetCellValue as getCellValue } from './pipeline/derive/CellValue';
import { RecomputeFullForRow } from './pipeline/derive/FullShipment';
import { ApplyFilters as applyFilters } from './pipeline/Filter';
import { useOperacoesSelector } from './pipeline/Selector';
import { SortRows as sortRows } from './pipeline/Sort';
import { OperacoesStoreProvider } from './pipeline/Store';
import { useCustomColumns } from './useCustomColumns';
import { useCustomPresets } from './useCustomPresets';
import { useUserPreferences } from './useUserPreferences';
import { useWarehouseConfig } from './useWarehouseConfig';
import { GetColumnDateRangeIso } from './utils/columnHelpers';

// periodRange já vem em UTC ISO dates do periodHelpers — não há mais
// conversão BR↔UTC no pipeline. As strings YYYY-MM-DD são UTC dates puras.

/**
 * Shape inicial de um preset novo (clicado em "+ Novo preset"). Mantém os
 * campos mínimos pra render: nome, categoria default, identificador SKU,
 * select como coluna fixa + algumas comuns pra começar.
 */
function makeBlankDraft() {
    return {
        id: null,
        name: 'Novo preset',
        category: 'Análise',
        identifier: 'sku',
        columns: ['select', 'sku', 'name', 'tags', 'bling', 'mlAvail', 'sales7', 'sales30'],
        filters: { period: 'last_30d' },
        sort: { col: 'sales30', dir: 'desc' },
    };
}

/**
 * Skeleton do carregamento inicial: aproxima o layout da tela (header de
 * colunas + linhas da tabela densa) com o Skeleton compartilhado do design
 * system, no lugar do antigo spinner custom. Renderizado só depois do delay
 * anti-flash do useDelayedLoading — cargas rápidas não piscam skeleton.
 */
function OperacoesLoadingSkeleton() {
    return (
        <div style={{ flex: 1, overflow: 'hidden', padding: '12px 16px' }} data-faid="Loading_Skeleton_Operacoes">
            <SkeletonTable columns={7} rows={12} />
        </div>
    );
}

const Operacoes = () => {
    const T = useTokens();
    const toast = useToast();
    const { user, checkPermission } = useAuth();
    const canManagePurchaseOrders = checkPermission('purchasing:create');

    const {
        allPresets,
        loading: presetsLoading,
        error: presetsError,
        reload: reloadPresets,
        createPreset,
        updatePreset,
        deletePreset,
    } = useCustomPresets();

    // Preferências cross-device do usuário (layoutMode + último preset usado).
    // Resizes/scroll/sort NÃO entram aqui — ficam em localStorage.
    const { preferences, loading: prefsLoading, setPreference } = useUserPreferences();

    // Colunas customizadas GLOBAIS (escopo org). Presets referenciam por
    // `custom:<id>` no array de columns; o Map é o resolver pra render/sort.
    const {
        columns: customColumns,
        byId: customColumnsById,
        loading: customColumnsLoading,
        createColumn: createCustomColumn,
        updateColumn: updateCustomColumn,
        deleteColumn: deleteCustomColumn,
    } = useCustomColumns();

    const [overrides, setOverrides] = useState({});
    // Inicia null porque presets são carregados async do backend. O effect de
    // hidratação abaixo escolhe o preset baseado em `operacoes_last_preset_id`
    // (com fallback pro primeiro) assim que presets + preferences carregarem.
    const [preset, setPreset] = useState(null);
    // Hydration tracking: a primeira escolha vem das preferências persistidas.
    // Depois disso, o effect só atua como fallback se o preset ativo virar null
    // (ex: usuário deletou o preset que estava usando) → escolhe primeiro.
    const presetHydratedRef = useRef(false);
    useEffect(() => {
        if (prefsLoading) return;
        if (preset) return;
        if (allPresets.length === 0) return;
        if (!presetHydratedRef.current) {
            const savedId = preferences.operacoes_last_preset_id;
            const saved = savedId ? allPresets.find((p) => p.id === savedId) : null;
            setPreset(saved || allPresets[0]);
            presetHydratedRef.current = true;
        } else {
            setPreset(allPresets[0]);
        }
    }, [allPresets, preset, prefsLoading, preferences.operacoes_last_preset_id]);
    // Estado de edição: null = não editando; objeto = draft em progresso.
    // O draft é uma cópia mutável; commit/cancel decidem se vai pro backend.
    const [editingDraft, setEditingDraft] = useState(null);
    const [savingPreset, setSavingPreset] = useState(false);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(null);
    const [layoutMode, setLayoutMode] = useState('vertical');
    // Mesma lógica do preset: hidrata uma vez quando preferences chega; depois
    // disso, mudanças do user passam por handleLayoutChange (que persiste). Ref
    // também é marcado quando o user troca antes do GET completar, evitando que
    // o valor recém-carregado sobrescreva a escolha que o user acabou de fazer.
    const layoutHydratedRef = useRef(false);
    useEffect(() => {
        if (layoutHydratedRef.current || prefsLoading) return;
        const saved = preferences.operacoes_layout_mode;
        // 'inline' foi removido — migra preferência legada pro equivalente
        // ('horizontal', que o inline imitava). Evita layout quebrado.
        if (saved) setLayoutMode(saved === 'inline' ? 'horizontal' : saved);
        layoutHydratedRef.current = true;
    }, [prefsLoading, preferences.operacoes_layout_mode]);
    const [cartOpen, setCartOpen] = useState(false);
    const [sortCol, setSortCol] = useState(null);
    const [sortDir, setSortDir] = useState('desc');
    const [generatingPurchaseOrders, setGeneratingPurchaseOrders] = useState(false);
    const [warehouseConfigOpen, setWarehouseConfigOpen] = useState(false);

    const warehouseConfig = useWarehouseConfig();

    // Em modo de edição, a tabela reflete o draft em progresso (não o preset salvo)
    // pra dar feedback ao vivo das mudanças. Fora de edição, usa o preset selecionado.
    const effectivePreset = editingDraft || preset;

    // Resolução do período efetivo (preset.filters.period + overrides) → range absoluto.
    // Em modo de edição, IGNORA overrides — chips estão escondidos e o builder na
    // lateral define os filtros diretamente.
    const effectivePeriod = editingDraft
        ? effectivePreset?.filters?.period || null
        : overrides.period || effectivePreset?.filters?.period || null;
    const periodRange = useMemo(() => {
        if (!effectivePeriod) {
            return null;
        }
        return ResolvePeriodRange(effectivePeriod, {
            lastNDays: overrides.lastNDays,
            dateFrom: overrides.dateFrom,
            dateTo: overrides.dateTo,
        });
    }, [effectivePeriod, overrides.lastNDays, overrides.dateFrom, overrides.dateTo]);

    const dateFrom = periodRange?.from || null;
    const dateTo = periodRange?.to || null;
    const periodDays = periodRange ? DaysBetween(periodRange.from, periodRange.to) : null;
    // periodRange.from/to já são UTC ISO dates (YYYY-MM-DD) — mesmo formato
    // dos buckets do backend e do anchorDateIso do Store. Passa direto.
    const periodStartIso = dateFrom || null;
    const periodEndIso = dateTo || null;

    // Deep-link via query param (usado pelo harness E2E e por links
    // compartilháveis): `?period=last_30d` ou `?from=YYYY-MM-DD&to=YYYY-MM-DD`
    // (custom), e `?brands=Marca1,Marca2` (filtro de marca). Aplica 1x quando o
    // preset ativo carrega — sobrevive ao reset de overrides da hidratação.
    const queryHydratedRef = useRef(false);
    useEffect(() => {
        if (queryHydratedRef.current || !effectivePreset) return;
        queryHydratedRef.current = true;
        const params = new URLSearchParams(window.location.search);
        const from = params.get('from');
        const to = params.get('to');
        const period = params.get('period');
        const brands = params.get('brands');
        const patch = {};
        if (from && to) {
            patch.period = 'custom';
            patch.dateFrom = from;
            patch.dateTo = to;
        } else if (period) {
            patch.period = period;
        }
        if (brands) {
            patch.brands = brands
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean);
        }
        if (Object.keys(patch).length > 0) {
            setOverrides((p) => ({ ...p, ...patch }));
        }
    }, [effectivePreset]);

    // ViewConfig consumido pelo pipeline novo (Selector). Sem wrapper —
    // index.jsx é o ÚNICO lugar que constrói esse objeto, derivado do preset
    // ativo + range do filtro de período (BR → UTC).
    const viewConfig = useMemo(() => {
        if (!periodStartIso || !periodEndIso) return null;
        return {
            identifier: effectivePreset?.identifier || 'sku',
            periodStartIso,
            periodEndIso,
            periodDays,
            // Bloco `preset.full` (snake_case) — Selector normaliza. Sem bloco = defaults.
            fullParams: effectivePreset?.full || null,
        };
    }, [effectivePreset?.identifier, effectivePreset?.full, periodStartIso, periodEndIso, periodDays]);

    const {
        rows: rawRows,
        loading,
        error,
        ordersCount,
        truncated: ordersTruncated,
        refetch: reload,
        brandOpts,
        tagOpts,
        allMlbRows,
    } = useOperacoesSelector(viewConfig);

    // Loading inicial (lista de presets OU dados do pipeline) vira UM estado só
    // pro skeleton não re-piscar na transição presets→dados. O useDelayedLoading
    // (300ms) segura o skeleton em cargas rápidas (anti-flash, plano UX 04).
    const initialLoading = (presetsLoading && !preset) || loading;
    const showLoadingSkeleton = useDelayedLoading(initialLoading);

    // updateRowLocal: edições de threshold/tags vão pro backend via API;
    // próximo refetch (worker re-tagueia) reflete na UI. Forçar refetch
    // dá feedback imediato.
    const updateRowLocal = useCallback(
        (sku, patch) => {
            if (sku && patch) reload();
        },
        [reload],
    );

    // Aplica filtro de depósitos selecionados antes do filterRows. Afeta `bling` (somatório
    // exibido na tabela/coluna), mas a lista completa em `depots` permanece para a aba Estoque.
    const rows = useMemo(() => {
        if (warehouseConfig.isAllSelected) {
            return rawRows;
        }
        // O Envio Full depende do bling (sugestão capada + tags) → re-roda só a
        // etapa 2 do derive com o bling filtrado, sem recomputar a série.
        const fullParams = effectivePreset?.full || null;
        return rawRows.map((row) =>
            RecomputeFullForRow(row, warehouseConfig.aggregatedTotal(row.depots || []), fullParams),
        );
    }, [rawRows, warehouseConfig, effectivePreset?.full]);

    // Range ISO UTC (YYYY-MM-DD) de cada coluna do preset — exposto no DOM
    // (Toolbar) pro harness E2E validar cada coluna no SEU range exato
    // (sales7 = anchor-6..anchor; periodTotal/orderCount = período ativo; etc),
    // sem o harness replicar a lógica de offset. anchorDateIso = "hoje" UTC do
    // servidor (asOf), igual ao bucketing do backend.
    const colRanges = useMemo(() => {
        const anchorIso = rows[0]?.anchorDateIso || periodEndIso || null;
        const cols = (effectivePreset?.columns || []).map((c) => (typeof c === 'string' ? c : c?.id)).filter(Boolean);
        const out = {};
        for (const col of cols) {
            const r = GetColumnDateRangeIso(col, { anchorIso, periodStartIso, periodEndIso });
            if (r) out[col] = r;
        }
        return out;
    }, [rows, effectivePreset, periodStartIso, periodEndIso]);

    /**
     * Map<id_str, name> derivado da config de depósitos — passado pra DetailPanel
     * → StockBreakdownPanel pra resolver "Estoque Principal" em vez de
     * "deposito:13772186008". Usa string ID porque o backend devolve referenceId
     * como string em alguns lugares e number em outros; padronizar como string
     * aqui evita lookup misses.
     */
    const warehouseNameById = useMemo(() => {
        const map = new Map();
        for (const w of warehouseConfig.warehouses || []) {
            const id = String(w.id || '').trim();
            if (id && w.name) map.set(id, w.name);
            // Também aceita lookup numérico (StockBreakdownPanel usa `toNumericId`).
            const numericId = Number.parseInt(id, 10);
            if (Number.isFinite(numericId) && w.name) map.set(numericId, w.name);
        }
        return map;
    }, [warehouseConfig.warehouses]);

    const {
        items: purchaseCart,
        addItem: addCartItem,
        updateItemQuantity: updateCartQuantity,
        removeItem: removeCartItem,
        clear: clearCart,
        payloadItems,
    } = usePurchaseCart();

    // Sincroniza sort com o preset ativo. Tolerante a preset=null (boot inicial).
    useEffect(() => {
        setSortCol(effectivePreset?.sort?.col || null);
        setSortDir(effectivePreset?.sort?.dir || 'desc');
    }, [effectivePreset]);

    // Guard centralizado de descarte de draft: sem draft, executa `run` direto;
    // com draft, arma o ConfirmModal destrutivo e só executa `run` quando o user
    // confirmar. Substitui os antigos window.confirm — centralizar evita repetir
    // o modal em 4 fluxos e garante mensagem consistente.
    const [discardConfirm, setDiscardConfirm] = useState(null); // { message, run }
    const requestDiscardDraft = useCallback(
        (run, message = 'Você tem alterações de preset não salvas. Descartar?') => {
            if (!editingDraft) {
                run();
                return;
            }
            setDiscardConfirm({ message, run });
        },
        [editingDraft],
    );

    const handlePresetChange = useCallback(
        (p) => {
            requestDiscardDraft(() => {
                setEditingDraft(null);
                setPreset(p);
                setOverrides({});
                setSelected(null);
                presetHydratedRef.current = true;
                setPreference('operacoes_last_preset_id', p?.id || null);
            });
        },
        [requestDiscardDraft, setPreference],
    );

    // Inicia edição de um preset existente: copia o objeto pra um draft mutável e
    // muda o preset ativo pro mesmo (pra tabela e draft ficarem sincronizados).
    const handleEditPreset = useCallback(
        (p) => {
            if (!p) return;
            requestDiscardDraft(() => {
                setPreset(p);
                setOverrides({});
                setSelected(null);
                setEditingDraft({ ...p });
            });
        },
        [requestDiscardDraft],
    );

    // Cria um draft em branco. Não mexe no `preset` salvo — vê o atual + edita o novo.
    const handleNewPreset = useCallback(() => {
        requestDiscardDraft(() => {
            setOverrides({});
            setSelected(null);
            setEditingDraft(makeBlankDraft());
        });
    }, [requestDiscardDraft]);

    // Descartar é uma ação explícita do user no builder → confirma sempre que há
    // draft (sem id, protege o "Novo preset" digitado; com id, as edições).
    const handleCancelEdit = useCallback(() => {
        requestDiscardDraft(() => setEditingDraft(null), 'Descartar as alterações deste preset?');
    }, [requestDiscardDraft]);

    // Commit do draft: cria (sem id) ou atualiza (com id). Em sucesso, fecha o
    // builder e seta o preset salvo como ativo. Em erro, mantém o draft pra retry.
    const handleSavePreset = useCallback(async () => {
        if (!editingDraft) return;
        setSavingPreset(true);
        try {
            let saved;
            if (editingDraft.id) {
                saved = await updatePreset(editingDraft.id, editingDraft);
            } else {
                saved = await createPreset(editingDraft);
            }
            setPreset(saved);
            setEditingDraft(null);
            // Persiste como último preset usado — relevante principalmente em
            // criação (preset novo recebe id só agora; sem isso o reload
            // perderia ele do "voltar no mesmo ponto").
            presetHydratedRef.current = true;
            setPreference('operacoes_last_preset_id', saved?.id || null);
            toast.success(editingDraft.id ? 'Preset atualizado' : 'Preset criado');
        } catch (err) {
            toast.error(err?.message || 'Erro ao salvar preset');
        } finally {
            setSavingPreset(false);
        }
    }, [editingDraft, createPreset, updatePreset, setPreference, toast]);

    const handleDeletePreset = useCallback(
        (p) => {
            if (!p?.id) return;
            const run = async () => {
                try {
                    await deletePreset(p.id);
                    toast.success('Preset excluído');
                    if (editingDraft?.id === p.id) {
                        setEditingDraft(null);
                    }
                    if (preset?.id === p.id) {
                        // Limpa antes do re-elect — useEffect escolherá próximo se houver,
                        // ou ficamos em estado "sem preset" (empty state renderiza CTA).
                        setPreset(null);
                    }
                } catch (err) {
                    toast.error(err?.message || 'Erro ao excluir preset');
                }
            };
            // Se o user está editando o preset que vai ser deletado, descarta o draft
            // (com confirmação) — sem isso, a UI fica com draft órfão referenciando id
            // que não existe mais no backend, e o save subsequente daria 404.
            if (editingDraft?.id === p.id) {
                requestDiscardDraft(run);
            } else {
                run();
            }
        },
        [deletePreset, preset?.id, editingDraft?.id, requestDiscardDraft, toast],
    );

    // Tri-state: desc → asc → none. Versão linear (sem nested setState).
    const handleSort = useCallback(
        (col) => {
            if (isColumnMasked(col, periodDays)) {
                return;
            }
            if (sortCol !== col) {
                setSortCol(col);
                setSortDir('desc');
                return;
            }
            if (sortDir === 'desc') {
                setSortDir('asc');
                return;
            }
            if (sortDir === 'asc') {
                // Volta pro padrão do preset (mantém ordenação útil em vez de "nenhuma").
                setSortCol(effectivePreset?.sort?.col || null);
                setSortDir(effectivePreset?.sort?.dir || 'desc');
                return;
            }
            setSortDir('desc');
        },
        [periodDays, sortCol, sortDir, effectivePreset],
    );

    const handleOverride = useCallback((key, val) => {
        setOverrides((prev) => ({ ...prev, [key]: val }));
    }, []);

    const handleRemoveOverride = useCallback((key) => {
        setOverrides((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    const filteredRows = useMemo(() => {
        if (!effectivePreset) {
            return rows;
        }
        // Overrides só aplicam fora de edição. Em edição, draft.filters é a fonte única.
        const eff = editingDraft
            ? { ...(effectivePreset.filters || {}) }
            : { ...(effectivePreset.filters || {}), ...overrides };
        if (search) {
            eff.search = search;
        }
        let result = applyFilters(rows, effectivePreset, eff);
        // Em preset MLB (identifier='mlb'), só exibir anúncios que VENDERAM
        // dentro do período do preset. Caso contrário aparecem MLBs com tag
        // "sem venda 30d" (sales históricas em 90d) que não fazem sentido em
        // contexto MLB (cada row = 1 anúncio = só vale se houve venda).
        // SKU mode mantém zerados (catálogo + tudo q tem estoque).
        if (effectivePreset.identifier === 'mlb' && periodDays > 0) {
            const w = Math.min(Math.max(1, periodDays), 90);
            result = result.filter((r) => {
                const daily = Array.isArray(r.dailyAll) ? r.dailyAll : [];
                if (daily.length === 0) return false;
                const start = daily.length - w;
                let sum = 0;
                for (let i = start; i < daily.length; i += 1) sum += Number(daily[i] || 0);
                return sum > 0;
            });
        }
        return result;
    }, [rows, effectivePreset, editingDraft, overrides, search, periodDays]);

    // ctx pra getCellValue resolver refs `custom:<id>` (sort/export precisam
    // do mesmo Map que a tabela usa pra render). Memoizado pra estabilidade
    // do useMemo do sortedRows e dos handlers.
    // ctx do getCellValue: customColumnsById pra refs `custom:<id>` +
    // periodDays pra avgPeriod/bestDay calcularem no mesmo período (sem isso
    // avgPeriod ficava cacheado do boot e podia ficar > bestDay).
    const cellCtx = useMemo(() => ({ customColumnsById, periodDays }), [customColumnsById, periodDays]);

    // sortedRows aplicado AQUI (não dentro da DataTable) para que a navegação <>
    // do DetailPanel siga a mesma ordem visual da tabela, independente da página.
    const sortedRows = useMemo(
        () => sortRows(filteredRows, sortCol, sortDir, periodDays, cellCtx),
        [filteredRows, sortCol, sortDir, periodDays, cellCtx],
    );

    // Cart items adapted to the local format (row + qty)
    const cartItems = useMemo(
        () =>
            purchaseCart.map((item) => ({
                row: {
                    sku: item.sku,
                    name: item.product_name || item.title || item.sku,
                    cost: Number(item.cost_price || 0),
                    bling: Number(item.bling_stock || 0),
                },
                qty: Number(item.quantity || 0),
            })),
        [purchaseCart],
    );

    /**
     * Map sku (UPPER) → { qty, key } — lookup O(1) usado pela coluna `select`
     * (input inline na DataTable). Recalculado quando o carrinho muda. Inclui
     * `key` pra `handleSetCartQty` poder fazer update direto via `updateItemQuantity`.
     */
    const cartBySku = useMemo(() => {
        const map = new Map();
        for (const item of purchaseCart) {
            const sku = String(item.sku || '')
                .trim()
                .toUpperCase();
            if (!sku) continue;
            map.set(sku, { qty: Number(item.quantity || 0), key: item.key });
        }
        return map;
    }, [purchaseCart]);

    /**
     * Atualiza qty no carrinho pra uma row da tabela. Cada keystroke do input
     * inline dispara isso (sem debounce). Vazio/zero remove; qty > 0 adiciona ou
     * atualiza. NÃO mostra toast — feedback visual é o próprio input (highlight
     * accent quando há valor).
     */
    const handleSetCartQty = useCallback(
        (row, qty) => {
            if (!canManagePurchaseOrders) {
                toast.error('Sem permissão para gerar OC');
                return;
            }
            const sku = String(row.sku || '')
                .trim()
                .toUpperCase();
            if (!sku) return;
            const existing = cartBySku.get(sku);
            const numeric = Math.max(0, Math.floor(Number(qty) || 0));
            if (numeric <= 0) {
                if (existing) removeCartItem(existing.key);
                return;
            }
            if (existing) {
                updateCartQuantity(existing.key, numeric);
            } else {
                addCartItem({
                    sku: row.sku,
                    product_name: row.name,
                    title: row.name,
                    cost_price: row.cost,
                    bling_stock: row.bling,
                    item_id: row.mlb,
                    item_ids: row.mlb ? [row.mlb] : [],
                    quantity: numeric,
                });
            }
        },
        [cartBySku, addCartItem, updateCartQuantity, removeCartItem, canManagePurchaseOrders, toast],
    );

    const handleAddCart = useCallback(
        (row) => {
            if (!canManagePurchaseOrders) {
                toast.error('Sem permissão para gerar OC');
                return;
            }
            const added = addCartItem({
                sku: row.sku,
                product_name: row.name,
                title: row.name,
                cost_price: row.cost,
                bling_stock: row.bling,
                item_id: row.mlb,
                item_ids: row.mlb ? [row.mlb] : [],
                quantity: 1,
            });
            if (!added) {
                toast.error('Não foi possível adicionar ao carrinho');
                return;
            }
            toast.success('Produto adicionado ao carrinho');
        },
        [addCartItem, canManagePurchaseOrders, toast],
    );

    const handleQtyChange = useCallback(
        (sku, qty) => {
            const target = String(sku || '').toUpperCase();
            const cartItem = purchaseCart.find((i) => String(i.sku || '').toUpperCase() === target);
            if (cartItem) {
                updateCartQuantity(cartItem.key, qty);
            }
        },
        [purchaseCart, updateCartQuantity],
    );

    const handleRemoveCart = useCallback(
        (sku) => {
            const target = String(sku || '').toUpperCase();
            const cartItem = purchaseCart.find((i) => String(i.sku || '').toUpperCase() === target);
            if (cartItem) {
                removeCartItem(cartItem.key);
            }
        },
        [purchaseCart, removeCartItem],
    );

    const handleConfirmCart = useCallback(async () => {
        if (purchaseCart.length === 0) {
            toast.error('Adicione ao menos um produto');
            return false;
        }
        setGeneratingPurchaseOrders(true);
        try {
            const response = await api.generatePurchaseOrders({ items: payloadItems });
            const createdCount = Number(response?.total || response?.results?.length || 0);
            toast.success(`${createdCount} OC(s) gerada(s) com sucesso`);
            return true;
        } catch (err) {
            toast.error(err?.message || 'Erro ao gerar ordens de compra');
            return false;
        } finally {
            setGeneratingPurchaseOrders(false);
        }
    }, [payloadItems, purchaseCart.length, toast]);

    // Finder overlay (Ctrl+F) — substitui o "Find in page" do browser pela
    // nossa busca custom (que filtra rows via fuzzy, não scroll/highlight).
    const [finderOpen, setFinderOpen] = useState(false);

    // Atalhos globais da página:
    //   Ctrl/Cmd+F → abre Finder overlay
    //   Ctrl/Cmd+S → escapa save-as do browser (operação é page-driven)
    useEffect(() => {
        const handler = (e) => {
            const isMod = e.ctrlKey || e.metaKey;
            if (isMod && (e.key === 's' || e.key === 'S')) {
                e.preventDefault();
            } else if (isMod && (e.key === 'f' || e.key === 'F')) {
                e.preventDefault();
                setFinderOpen(true);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    const handleLayoutChange = useCallback(
        (mode) => {
            setLayoutMode(mode);
            layoutHydratedRef.current = true;
            setPreference('operacoes_layout_mode', mode);
        },
        [setPreference],
    );

    const handleSelect = useCallback((row) => {
        // Em modo MLB row.sku pode ser nulo — usa row.id como chave.
        const next = row?.id || row?.sku;
        setSelected((prev) => ((prev?.id || prev?.sku) === next ? null : row));
    }, []);

    const handleExport = useCallback(() => {
        if (!effectivePreset) return;
        const cols = (effectivePreset.columns || [])
            .map((c) => (typeof c === 'string' ? c : c.id))
            .filter((c) => c !== 'select' && c !== 'trend' && c !== 'tags');
        const headers = cols.map((c) => {
            const custom = Array.isArray(effectivePreset.columns)
                ? effectivePreset.columns.find((col) => col?.id === c)
                : null;
            return custom?.name || c;
        });

        // Colunas tratadas como NUMÉRICAS no export — formato BR (5734,91 / 0,00,
        // sempre 2 decimais, sem separador de milhar). Demais (sku/name/mlb/brand/
        // tags/etc) saem como string crua.
        const NUMERIC_COLS = new Set([
            'bling',
            'mlAvail',
            'mlUnavail',
            'mlTotal',
            'stockTotal',
            'sales_today',
            'sales_yday',
            'sales3',
            'sales5',
            'sales7',
            'sales30',
            'avgPeriod',
            'week1',
            'week2',
            'week3',
            'week4',
            'month1',
            'month2',
            'month3',
            'coverage',
            'fullCoverage',
            'fullNeed',
            'fullSuggest',
            'bestDay',
            'price',
            'cost',
            'margin',
        ]);
        const fmtBr = (n) => {
            const num = Number(n);
            if (!Number.isFinite(num)) return '0,00';
            return num.toFixed(2).replace('.', ',');
        };
        const valueFor = (row, col) => {
            // Custom expression cols ficam fora de NUMERIC_COLS — usa getCellValue
            // (com cellCtx) que resolve `custom:<id>` via expressionEngine.
            if (NUMERIC_COLS.has(col)) {
                let v = getCellValue(row, col, cellCtx);
                // null = "sem valor" (ex: cobertura Full sem demanda) → célula vazia,
                // não "0,00" (zero significaria cobertura zerada).
                if (v === null || v === undefined) return '';
                // getCellValue pode devolver string formatada ("R$ X,YZ" / "10%").
                // Tira prefixo/sufixo e converte vírgula → ponto pra parsear como JS number.
                if (typeof v === 'string') {
                    v = v
                        .replace(/^R\$\s*/, '')
                        .replace(/%$/, '')
                        .replace(/\./g, '')
                        .replace(',', '.');
                }
                return fmtBr(v || 0);
            }
            const raw = getCellValue(row, col);
            if (raw === null || raw === undefined) return '';
            // Arrays (ex: tags) — junta com vírgula. Demais converte com String().
            if (Array.isArray(raw)) return raw.join(', ');
            return String(raw);
        };

        // CSV pt-BR: separador `;` (não conflita com decimais), `sep=;` na primeira
        // linha pro Excel saber, BOM UTF-8 (﻿) preserva acentos, CRLF entre linhas.
        const sep = ';';
        const quote = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const lines = [
            `sep=${sep}`,
            headers.map(quote).join(sep),
            ...filteredRows.map((row) => cols.map((c) => quote(valueFor(row, c))).join(sep)),
        ];
        const csv = `﻿${lines.join('\r\n')}`;
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Filename = range de datas legível. Excel usa o filename (sem .csv,
        // até 31 chars) como nome da aba — então "01-12-2025 até 22-05-2026"
        // aparece direto no rótulo da planilha em vez do UUID truncado.
        // Fallback: data de hoje quando não há range definido.
        // CSV filename usa UTC (consistente com o resto do pipeline).
        const todayBR = (() => {
            const d = new Date();
            return `${String(d.getUTCDate()).padStart(2, '0')}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${d.getUTCFullYear()}`;
        })();
        const toBr = (iso) => {
            if (!iso) return null;
            const parts = String(iso).split('-');
            if (parts.length !== 3) return null;
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        };
        const fromStr = toBr(dateFrom);
        const toStr = toBr(dateTo);
        const fileName = fromStr && toStr ? `${fromStr} até ${toStr}.csv` : `operacoes ${todayBR}.csv`;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
    }, [filteredRows, effectivePreset, dateFrom, dateTo, cellCtx]);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
                width: '100%',
                overflow: 'hidden',
                fontFamily: T.sans,
                background: T.bgSoft,
            }}
            // `op-scroll` aplica a scrollbar customizada (CSS no <style> abaixo)
            // a TODOS os descendentes scrolláveis: tabela, builder, dropdowns, etc.
            className="op-scroll"
            data-faid="Page_Container_Operacoes"
        >
            <Toolbar
                preset={effectivePreset}
                periodRange={periodRange}
                colRanges={colRanges}
                presets={allPresets}
                onPresetChange={handlePresetChange}
                onEditPreset={handleEditPreset}
                onNewPreset={handleNewPreset}
                onDeletePreset={handleDeletePreset}
                editing={editingDraft}
                currentUserId={user?.id || user?.email || null}
                layoutMode={layoutMode}
                onLayoutChange={handleLayoutChange}
                overrides={overrides}
                onOverride={handleOverride}
                onRemoveOverride={handleRemoveOverride}
                brandOpts={brandOpts}
                tagOpts={tagOpts}
                onExport={handleExport}
                search={search}
                onSearchChange={setSearch}
                onOpenWarehouseConfig={() => setWarehouseConfigOpen(true)}
                cartCount={cartItems.reduce((s, i) => s + i.qty, 0)}
                onOpenCart={() => setCartOpen(true)}
                showCart={canManagePurchaseOrders}
                onReload={reload}
                reloading={loading}
                warehouses={warehouseConfig.warehouses || []}
            />

            {presetsLoading && !preset ? (
                showLoadingSkeleton ? (
                    <OperacoesLoadingSkeleton />
                ) : null
            ) : presetsError ? (
                <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: T.warn, fontSize: 13 }}>
                    <div style={{ textAlign: 'center', lineHeight: 1.7, maxWidth: 420 }}>
                        <div style={{ fontSize: 14, fontWeight: 500, color: T.ink }}>
                            {typeof presetsError === 'string' ? presetsError : presetsError.message}
                        </div>
                        {typeof presetsError === 'object' && presetsError?.code && (
                            <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono, marginTop: 6 }}>
                                código: {presetsError.code}
                            </div>
                        )}
                        <div style={{ marginTop: 16 }}>
                            <button
                                type="button"
                                onClick={reloadPresets}
                                style={{
                                    background: T.ink,
                                    color: T.line,
                                    border: 'none',
                                    padding: '6px 14px',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    fontSize: 13,
                                }}
                            >
                                Tentar novamente
                            </button>
                        </div>
                    </div>
                </div>
            ) : loading ? (
                showLoadingSkeleton ? (
                    <OperacoesLoadingSkeleton />
                ) : null
            ) : error ? (
                <div
                    style={{
                        flex: 1,
                        display: 'grid',
                        placeItems: 'center',
                        color: T.warn,
                        fontSize: 13,
                    }}
                >
                    <div style={{ textAlign: 'center', lineHeight: 1.7, maxWidth: 420 }}>
                        <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'center' }}>
                            <Warning size={28} color={T.warn} />
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 500, color: T.ink }}>
                            {typeof error === 'string' ? error : error.message}
                        </div>
                        {typeof error === 'object' && error?.code && (
                            <div
                                style={{
                                    fontSize: 11,
                                    color: T.muted,
                                    fontFamily: T.mono,
                                    marginTop: 6,
                                }}
                            >
                                código: {error.code}
                            </div>
                        )}
                        <div style={{ marginTop: 16 }}>
                            <button
                                type="button"
                                onClick={reload}
                                style={{
                                    background: T.ink,
                                    color: T.line,
                                    border: 'none',
                                    padding: '6px 14px',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    fontSize: 13,
                                }}
                            >
                                Tentar novamente
                            </button>
                        </div>
                    </div>
                </div>
            ) : !effectivePreset ? (
                // Lista de presets carregou mas está vazia (e nenhum draft em
                // andamento). Mostra CTA pra criar o primeiro. Esse estado também
                // aparece se o user apagar o último preset.
                <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: T.muted, fontSize: 13 }}>
                    <div style={{ textAlign: 'center', lineHeight: 1.7, maxWidth: 420 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: T.ink, marginBottom: 6 }}>
                            Nenhum preset configurado
                        </div>
                        <div style={{ fontSize: 13, color: T.muted, marginBottom: 16 }}>
                            Presets definem as colunas, filtros e ordenação da tabela. Crie o primeiro pra começar.
                        </div>
                        <button
                            type="button"
                            onClick={handleNewPreset}
                            style={{
                                background: T.ink,
                                color: '#fff',
                                border: 'none',
                                padding: '8px 18px',
                                borderRadius: 4,
                                cursor: 'pointer',
                                fontSize: 13,
                                fontWeight: 500,
                            }}
                        >
                            + Criar primeiro preset
                        </button>
                    </div>
                </div>
            ) : (
                <Workspace
                    layoutMode={layoutMode}
                    preset={effectivePreset}
                    filteredRows={sortedRows}
                    selected={selected}
                    onSelect={handleSelect}
                    onAddCart={canManagePurchaseOrders ? handleAddCart : null}
                    sortCol={sortCol}
                    sortDir={sortDir}
                    onSort={handleSort}
                    periodDays={periodDays}
                    ordersCount={ordersCount}
                    ordersTruncated={ordersTruncated}
                    onUpdateRow={updateRowLocal}
                    cartBySku={cartBySku}
                    onSetCartQty={canManagePurchaseOrders ? handleSetCartQty : null}
                    warehouseNameById={warehouseNameById}
                    customColumnsById={customColumnsById}
                    allMlbRows={allMlbRows}
                    presetIdentifier={effectivePreset?.identifier || 'sku'}
                    periodStartIso={periodStartIso}
                    periodEndIso={periodEndIso}
                    editing={Boolean(editingDraft)}
                    onReorderColumns={(nextCols) =>
                        editingDraft && setEditingDraft({ ...editingDraft, columns: nextCols })
                    }
                    builderNode={
                        editingDraft ? (
                            <PresetBuilder
                                draft={editingDraft}
                                onChange={setEditingDraft}
                                onCancel={handleCancelEdit}
                                onSave={handleSavePreset}
                                brandOpts={brandOpts}
                                tagOpts={tagOpts}
                                warehouses={warehouseConfig.warehouses || []}
                                isNew={!editingDraft.id}
                                saving={savingPreset}
                                canSavePersonal={Boolean(user?.id || user?.email)}
                                customColumns={customColumns}
                                customColumnsLoading={customColumnsLoading}
                                onCreateCustomColumn={createCustomColumn}
                                onUpdateCustomColumn={updateCustomColumn}
                                onDeleteCustomColumn={deleteCustomColumn}
                            />
                        ) : null
                    }
                />
            )}

            {/* Confirmação de descarte de draft (trocar/editar/criar/excluir preset
                com edição em andamento, e o "Descartar" do builder) — ConfirmModal
                do design system no lugar dos antigos window.confirm. */}
            <ConfirmModal
                isOpen={Boolean(discardConfirm)}
                onClose={() => setDiscardConfirm(null)}
                onConfirm={() => {
                    const pending = discardConfirm;
                    setDiscardConfirm(null);
                    pending?.run();
                }}
                title="Descartar alterações"
                message={discardConfirm?.message}
                confirmText="Descartar"
                confirmVariant="danger"
            />

            {canManagePurchaseOrders && (
                <CartModal
                    open={cartOpen}
                    onClose={() => setCartOpen(false)}
                    items={cartItems}
                    onQtyChange={handleQtyChange}
                    onRemove={handleRemoveCart}
                    onClear={clearCart}
                    onConfirm={handleConfirmCart}
                    generating={generatingPurchaseOrders}
                />
            )}

            <WarehouseConfigModal
                open={warehouseConfigOpen}
                onClose={() => setWarehouseConfigOpen(false)}
                warehouses={warehouseConfig.warehouses}
                loading={warehouseConfig.loading}
                selectedIds={warehouseConfig.selectedIds}
                isAllSelected={warehouseConfig.isAllSelected}
                onApply={warehouseConfig.setSelection}
            />

            <FinderOverlay
                open={finderOpen}
                initialValue={search}
                onCommit={(v) => setSearch(v)}
                onClose={() => setFinderOpen(false)}
            />

            <style>{`
                @keyframes slideIn {
                    from { transform: translateX(40px); opacity: 0; }
                    to   { transform: translateX(0);    opacity: 1; }
                }
                @keyframes opSpin {
                    to { transform: rotate(360deg); }
                }
                @keyframes opModalIn {
                    from { transform: translate(-50%, -46%); opacity: 0; }
                    to   { transform: translate(-50%, -50%); opacity: 1; }
                }
            `}</style>
        </div>
    );
};

/**
 * Wrap externo: o OperacoesStoreProvider sobe pro topo da rota pra que o
 * pipeline (Loader fetch + Store cache + Selector derive) viva acima do
 * componente Operacoes. Trocar de preset/filtro não dispara refetch — só
 * o Selector re-deriva. Único refetch acontece em refetch() (botão
 * Atualizar) ou quando o preset pede um range maior que o carregado.
 */
function OperacoesWithProvider() {
    return (
        <OperacoesStoreProvider>
            <Operacoes />
        </OperacoesStoreProvider>
    );
}

export default OperacoesWithProvider;
